import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';

import { feSetStylistId, feSidebarClose } from 'actions/ui.actions';
import IMG_SETTINGS from 'assets/images/menu/building.svg';
import IMG_CLIENT_TAGS from 'assets/images/menu/clienttags-black.svg';
import IMG_WEBSITES from 'assets/images/menu/computer-black.svg';
import IMG_CARD from 'assets/images/menu/credit-card.svg';
import IMB_GIFT_VOUCHER_ICON from 'assets/images/menu/giftvouchers-black.svg';
import IMG_HR_REPORTS from 'assets/images/menu/hr-black.svg';
import IMG_CLIENT_COMMUNICATIONS from 'assets/images/menu/marketing-black.svg';
import IMG_ONLINE from 'assets/images/menu/onlinebooking-black.svg';
import IMG_PRICE_LIST_ICON from 'assets/images/menu/pricelist-black.svg';
import IMG_REPORTS from 'assets/images/menu/reports-black.svg';
import IMG_STOCK_MANAGEMENT from 'assets/images/menu/stock-black.svg';
import IMG_SUPPORT from 'assets/images/menu/support-white.svg';
import IMG_HR_ICON from 'assets/images/menu/user-group-black.svg';
import { fromPathnameToIsEditAppointment, fromPathnameToIsStylistsSettings } from 'bl/pathname.bl';
import Permitted from 'components/core/Permitted.component';
import SalonFlag from 'components/core/SalonFlag.component';
import ToggleFeature from 'components/core/ToggleFeature.component';
import WhenSalonFlag from 'components/core/WhenSalonFlag.component';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import Badge from 'components/generic/Badge.component';
import Icon from 'components/generic/Icon.component';
import IconStatus from 'components/generic/IconStatus.component';
import Space from 'components/generic/Space.component';
import Tooltip from 'components/generic/Tooltip.component';
import AllowedMenuItem from 'components/menu/AllowedMenuItem.component';
import MenuReceptionistItem from 'components/menu/MenuReceptionistItem.component';
import NavLinkToCalendar from 'components/menu/NavLinkToCalendar.component';
import NewBanner from 'components/menu/NewBanner.component';
import { ReleaseMenuBanner } from 'components/menu/ReleaseMenuBanner.component';
import { SidebarFooter } from 'components/menu/SidebarFooter.component';
import SlickPayLink from 'components/menu/SlickPayLink.component';
import SM from 'components/sidebar/appointment/wizard/Wizard.module.scss';
import LSK from 'constants/core/localStorageKey.const';
import PERM from 'constants/core/perm.const';
import { CURRENCY_BORDER_ICONS } from 'constants/currency.const';
import LINK from 'constants/link.const';
import SF from 'constants/salonFlag.const';
import S from 'constants/string.const';
import SP from 'constants/ui/sidePanel.const';
import { useCountryImage } from 'hooks/useCountryImage.hook';
import useIsFromMobileApp from 'hooks/useIsFromMobileApp.hook';
import { usePathname } from 'hooks/usePathname.hook';
import useTip from 'hooks/useTip.hook';
import { useGilt, useTranslation } from 'providers/GILT.provider';
import { useSalonBasicInfo } from 'providers/SalonBasicInfo.provider';
import { useThemeWithBright } from 'providers/Theme.provider';
import { selectBookingAddedServices } from 'selectors/booking.selectors';
import {
  checkHideStockForReceptionist,
  selectCurrentSalonBetaAnnualSalon,
  selectCurrentSalonEnabledPinAccess,
  selectCurrentSalonHasMissedInvoice,
  selectCurrentSalonHasNewSalonAccess,
  selectCurrentSalonPricingPlan,
} from 'selectors/currentSalon.selectors';
import { selectOldFlagsPincodes } from 'selectors/oldFlags.selectors';
import { selectSalonStylistListEmailsWithLogin, selectSalonStylistListRemovedAccess } from 'selectors/salon.selectors';
import { selectSchedulerStylistId } from 'selectors/scheduler.selectors';
import selectEffectiveUserInfo from 'selectors/selectEffectiveUserInfo';
import { selectSidebarPanel } from 'selectors/sidebar.selectors';
import asArray from 'utils/arrays/asArray.util';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import { nowAsIso } from 'utils/date/now.util';
import lsSet from 'utils/dom/storage/lsSet.util';
import alwaysNoop from 'utils/functions/nullary/alwaysNoop.util';
import linkTo from 'utils/links/linkTo.util';
import logger from 'utils/logger.util';
import truncateString from 'utils/strings/truncateString.util';

import { getUserPermission, removeAccess, resetRemovedAccess } from 'salon/actions';
import PincodeExistingEmailPopup from 'settings/stylists/PincodeExistingEmailPopup';

import CN from './SidebarMenu.module.scss';

const L = logger('SidebarMenu');

const USE_MENU_ITEM_WEBSITES = false;
const USE_NEW_FEATURE_BADGE = false;
const USE_PIN_POPUP = false;

const isNotDeleted = ({ deleted }) => deleted !== true;

const SidebarMenu = ({ showMenu: showMenuFromProps, isInClientTags } = {}) => {
  const { t } = useTranslation();
  const { isSalonInGB } = useGilt();
  const { salonId, salonName } = useSalonBasicInfo();
  const isFromMobile = useIsFromMobileApp();
  const { isDarkMode } = useThemeWithBright();
  const pathname = usePathname();
  const dispatch = useDispatch();

  const addedServices = useSelector(selectBookingAddedServices);
  const enablePincodes = useSelector(selectOldFlagsPincodes);
  const enabledPinAccess = useSelector(selectCurrentSalonEnabledPinAccess);
  const hasMissedInvoice = useSelector(selectCurrentSalonHasMissedInvoice);
  const hasNewSalonAccess = useSelector(selectCurrentSalonHasNewSalonAccess);
  const pricingPlan = useSelector(selectCurrentSalonPricingPlan);
  const removedAccess = useSelector(selectSalonStylistListRemovedAccess);
  const emailsWithLogin = useSelector(selectSalonStylistListEmailsWithLogin);
  const selectedStylistId = useSelector(selectSchedulerStylistId);
  const userRole = useSelector((state) => selectEffectiveUserInfo(state).role);
  const isHideStockForReceptionist = useSelector(checkHideStockForReceptionist);
  const hasBetaAnnualSalon = useSelector(selectCurrentSalonBetaAnnualSalon);
  const panel = useSelector(selectSidebarPanel);

  const { val: isMenuOpen, nay: closeMenu, tip: toggleMenu } = useTip(showMenuFromProps || false);
  const { val: isPinShown, yay: showPin, nay: hidePin } = useTip(false);

  const { val: renderExitModal, yay: showExitModal, nay: closeExitModal } = useTip(false);

  const menuCashupIcon = useCountryImage(CURRENCY_BORDER_ICONS);

  const isPinNeeded = fromAnyToBool(enablePincodes && enabledPinAccess);
  const isSidebarOpen = fromAnyToBool(isInClientTags ? showMenuFromProps : isMenuOpen);

  const params = Object.freeze({ salonId, id: salonId });
  const supportLink = linkTo({ pattern: LINK.support, params, fault: '#' });
  const cashupLink = linkTo({ pattern: LINK.cashup, params, fault: '#' });
  const kpiLink = linkTo({ pattern: LINK.slnStaffReportsKpi, params, fault: '#' });
  const hrSplashLink = linkTo({ pattern: LINK.hrSplash, params });
  const hrLink = linkTo({ pattern: LINK.hrEditKpi, params });
  const bucketsLink = linkTo({ pattern: LINK.reportsBuckets, params, fault: '#' });
  const tagsLink = linkTo({ pattern: LINK.slnTags, params });
  const portfolioLink = linkTo({ pattern: LINK.portfolio, params, fault: '#' });
  const stocksManagerLink = linkTo({ pattern: LINK.stocksManager, params });
  const vouchersLink = linkTo({ pattern: LINK.vouchersTracking, params });
  const depositsLink = `/salon/${salonId}/deposits`;
  const bookingsLink = `/salon/${salonId}/onlinebooking/settings`;
  const salonLink = `/salon/${salonId}/settings/salon`;
  const priceLink = `/salon/${salonId}/price-list`;

  const TITLE_CASHUP = useMemo(() => t('composite.sidebarMenu.dailyCashupLabel'), [t]);
  const TITLE_KPI = useMemo(() => t('composite.sidebarMenu.staffKpisLabel'), [t]);

  const handleClosePin = useCallback(() => {
    closeMenu();
    hidePin();
  }, [closeMenu, hidePin]);

  const handleToggle = useCallback(() => {
    if (fromPathnameToIsEditAppointment(pathname) || asArray(addedServices).filter(isNotDeleted).length > 0) {
      showExitModal();
      return;
    }
    if (USE_PIN_POPUP && fromPathnameToIsStylistsSettings(pathname) && isPinNeeded) {
      showPin();
    } else {
      if (selectedStylistId && panel === SP.stylistLog) {
        dispatch(feSidebarClose({ tag: 'SidebarMenu.handleToggle()' }));
        dispatch(feSetStylistId(null));
      }

      toggleMenu();
    }
  }, [addedServices, dispatch, isPinNeeded, panel, pathname, selectedStylistId, showExitModal, showPin, toggleMenu]);

  useEffect(() => {
    L.unignore('useEffect()', 'getUserPermission:', dispatch(getUserPermission(salonId)));
    lsSet(LSK.expandedCatsIds, '');
    lsSet(LSK.expandedServIds, '');
  }, [dispatch, salonId]);

  L.debug('()', nowAsIso(), isMenuOpen);

  return (
    <>
      <div
        data-bem="SidebarMenu"
        className={cn({
          [CN.component]: true,
          [CN.darkMode]: isDarkMode,
        })}
      >
        {!isFromMobile &&
          ((hasMissedInvoice && !hasBetaAnnualSalon) || !hasNewSalonAccess ? null : (
            <div data-bem="SidebarMenu__button" className={CN.navButton} onClick={handleToggle} onKeyDown={alwaysNoop} role="button" tabIndex="0">
              <div className={CN.menuText}>{t('composite.sidebarMenu.title')}</div>
            </div>
          ))}

        {isSidebarOpen && (
          <div
            data-bem="SidebarMenu__container"
            className={cn({
              [CN.nav]: true,
              [CN.visible]: isSidebarOpen,
            })}
          >
            <div className={CN.scroll}>
              <div className={CN.header}>
                <div className={CN.headerText}>
                  <span className={CN.textBold}>{t('composite.sidebarMenu.location')}</span>
                  <div data-bem="SidebarMenu__name" className={CN.salonNameContainer}>
                    <Tooltip variant="bottom" content={`${t('composite.sidebarMenu.paymentPlan')} ${pricingPlan || t('common.custom')}`}>
                      <Space>{truncateString({ value: salonName, max: 24, suffix: '...' })}</Space>
                      <Space>({salonId})</Space>
                    </Tooltip>
                  </div>
                </div>

                <div data-bem="SidebarMenu__access" className={CN.headerText}>
                  {t('composite.sidebarMenu.accessLevel')}
                  <span className={CN.textCap}>{userRole}</span>
                </div>

                <ReleaseMenuBanner />

                <div data-bem="SidebarMenu__close" className={CN.closeContainer}>
                  <Button variant="empty" onClick={closeMenu}>
                    {isDarkMode ? <Icon variant="close" /> : <Icon variant="close" variantInvert />}
                  </Button>
                </div>
              </div>

              <div data-bem="SidebarMenu__links" className={CN.container}>
                {isSalonInGB && (
                  <NavLink to={supportLink} activeClassName="active" className={CN.supportItem} onClick={closeMenu}>
                    <div className={CN.supportItemContainer}>
                      <img src={IMG_SUPPORT} alt="" className={CN.menuIcon} />
                      {t('composite.sidebarMenu.newBannerText')}
                    </div>
                    <div className={CN.supportItemLabel}>
                      {t('composite.sidebarMenu.new')} {S.rocket}
                    </div>
                  </NavLink>
                )}

                <NavLinkToCalendar onClick={closeMenu} />

                <SlickPayLink />

                <Permitted value={PERM.canViewDailyCashupPersonal} tag="SidebarMenu__cashup--own">
                  <AllowedMenuItem to={cashupLink} icon={menuCashupIcon} title={TITLE_CASHUP} onClick={closeMenu} />
                </Permitted>

                <Permitted value={PERM.canViewDailyCashupAllStaff} tag="SidebarMenu__cashup--all">
                  <AllowedMenuItem to={cashupLink} icon={menuCashupIcon} title={TITLE_CASHUP} onClick={closeMenu} />
                </Permitted>

                <Permitted value={PERM.canViewKpiReportsPersonal} tag="SidebarMenu__kpi--own">
                  <AllowedMenuItem to={kpiLink} icon={IMG_REPORTS} title={TITLE_KPI} onClick={closeMenu} />
                </Permitted>

                <Permitted value={PERM.canViewKpiReportsAllStaff} tag="SidebarMenu__kpi--all">
                  <AllowedMenuItem to={kpiLink} icon={IMG_REPORTS} title={TITLE_KPI} onClick={closeMenu} />
                </Permitted>

                <Permitted value={PERM.canViewKpiReportsAllStaff}>
                  <AllowedMenuItem to={bucketsLink} icon={IMG_HR_REPORTS} title={t('composite.sidebarMenu.businessReportsLabel')} onClick={closeMenu} />
                </Permitted>

                <NavLink to={bookingsLink} activeClassName="active" className={CN.item} onClick={closeMenu}>
                  <img src={IMG_ONLINE} alt="Menu icon Vouchers & Payments" className={CN.menuIcon} />
                  {t('composite.sidebarMenu.onlineBookingLabel')}
                </NavLink>

                <MenuReceptionistItem
                  icon={IMG_CLIENT_COMMUNICATIONS}
                  title={t('composite.sidebarMenu.clientsNotificationsAndMarketingLabel')}
                  navLink={linkTo({
                    pattern: LINK.marketing,
                    params,
                  })}
                  onClick={closeMenu}
                >
                  <SalonFlag name={SF.emailMarketing}>
                    <SalonFlag.IfEnabled>
                      <div className={CN.menuBadgeGap}>
                        <Badge name={`${t('composite.sidebarMenu.new')} ${S.rocket}`} variant="menu" />
                      </div>
                    </SalonFlag.IfEnabled>
                  </SalonFlag>
                </MenuReceptionistItem>

                {/* switching providers so not currently offering this as an option, so just hide the whole thing */}
                {USE_MENU_ITEM_WEBSITES && (
                  <NavLink to={portfolioLink} activeClassName="active" className={CN.item} onClick={closeMenu}>
                    <img src={IMG_WEBSITES} alt="Menu icon portfolio" className={CN.menuIcon} />
                    {t('composite.sidebarMenu.designWebsiteLabel')}
                    <div className={CN.labelMenu}>
                      <NewBanner />
                    </div>
                  </NavLink>
                )}

                <NavLink to={tagsLink} activeClassName="active" className={CN.item} onClick={closeMenu}>
                  <img src={IMG_CLIENT_TAGS} alt="Menu icon client tags" className={CN.menuIcon} />
                  {t('composite.sidebarMenu.clientTagsLabel')}
                </NavLink>

                <WhenSalonFlag disabled name={SF.timeOffCats}>
                  <NavLink to={hrSplashLink} activeClassName="active" className={CN.item} onClick={closeMenu}>
                    <img src={IMG_HR_ICON} alt="Menu icon staff" className={CN.menuIcon} />
                    {t('composite.sidebarMenu.teamManagementLabel')}
                    {USE_NEW_FEATURE_BADGE && (
                      <div className={CN.gapContainer}>
                        <Badge name={`${t('composite.sidebarMenu.new')} ${S.rocket}`} variant="menu" />
                      </div>
                    )}
                  </NavLink>
                </WhenSalonFlag>
                <WhenSalonFlag name={SF.timeOffCats}>
                  <NavLink to={hrLink} activeClassName="active" className={CN.item} onClick={closeMenu}>
                    <img src={IMG_HR_ICON} alt="Menu icon staff" className={CN.menuIcon} />
                    {t('composite.sidebarMenu.teamManagementLabel')}
                  </NavLink>
                </WhenSalonFlag>

                <ToggleFeature flag="stockManagement">
                  {isHideStockForReceptionist ? (
                    <MenuReceptionistItem
                      icon={IMG_STOCK_MANAGEMENT}
                      title={t('composite.sidebarMenu.stockManagementLabel')}
                      navLink={linkTo({ pattern: LINK.stocksManager, params })}
                      onClick={closeMenu}
                    />
                  ) : (
                    <NavLink to={stocksManagerLink} activeClassName="active" className={CN.item} onClick={closeMenu}>
                      <img src={IMG_STOCK_MANAGEMENT} alt="Menu icon Stock" className={CN.menuIcon} />
                      {t('composite.sidebarMenu.stockManagementLabel')}
                    </NavLink>
                  )}
                </ToggleFeature>
                <NavLink to={depositsLink} activeClassName="active" className={CN.item} onClick={closeMenu}>
                  <img src={IMG_CARD} alt="Menu icon deposits" className={CN.menuIcon} />
                  {t('composite.sidebarMenu.clientDepositsLabel')}
                </NavLink>
                <NavLink to={vouchersLink} activeClassName="active" className={CN.item} onClick={closeMenu}>
                  <img src={IMB_GIFT_VOUCHER_ICON} alt="Menu icon vouchers" className={CN.menuIcon} />
                  {t('composite.sidebarMenu.giftVoucherTrackingLabel')}
                </NavLink>
                <NavLink to={salonLink} activeClassName="active" onClick={closeMenu} className={CN.item}>
                  <img src={IMG_SETTINGS} alt="Menu icon settings" className={CN.menuIcon} />
                  {t('composite.sidebarMenu.staffAndBusinessSettingsLabel')}
                </NavLink>
                <NavLink to={priceLink} activeClassName="active" className={CN.item} onClick={closeMenu}>
                  <img src={IMG_PRICE_LIST_ICON} alt="Menu icon price List" className={CN.menuIcon} />
                  {t('composite.sidebarMenu.servicePriceListLabel')}
                </NavLink>
              </div>
            </div>
            <SidebarFooter />
          </div>
        )}
        <div className={fromAnyToBool(isSidebarOpen) ? CN.overlay : ''} />
      </div>

      {USE_PIN_POPUP && isPinShown && enablePincodes && (
        <PincodeExistingEmailPopup
          emailsWithLogin={emailsWithLogin}
          closePopup={handleClosePin}
          removeAccess={(args) => dispatch(removeAccess(args))}
          salonId={salonId}
          removedAccess={removedAccess}
          resetRemovedAccess={() => dispatch(resetRemovedAccess())}
          closePinPopup={handleClosePin}
          toggleMenu={handleToggle}
        />
      )}

      <SalonModal tag="SidebarMenu__modal--exit" isOpen={renderExitModal} withFooter>
        <SalonModal.Body>
          <IconStatus variant="alert" width="lg">
            <Icon variant="alert" width="l1" />
          </IconStatus>
          <div className={SM.modalContent}>
            <div className={SM.modalTitle}>{t('composite.sidebarMenu.finishBookingModal.title')}</div>
            <span className={SM.modalText} data-bem="SidebarMenu__text">
              {t('composite.sidebarMenu.finishBookingModal.description')}
            </span>
          </div>
        </SalonModal.Body>
        <SalonModal.Footer>
          <Button tag="SidebarMenu__button-ok" variant="primaryForce" onClick={closeExitModal}>
            <span className={SM.buttonSpace}>{t('common.buttons.ok')}</span>
          </Button>
        </SalonModal.Footer>
      </SalonModal>
    </>
  );
};

export default SidebarMenu;
